import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import api from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#001F3F]">
        <div className="w-full max-w-md text-center">
          <h1 className="font-display text-3xl font-bold uppercase tracking-tighter text-white">
            Lien invalide
          </h1>
          <p className="mt-3 text-sm text-white/65">
            Le lien de réinitialisation est invalide ou manquant.
          </p>
          <Link
            to="/forgot-password"
            className="mt-8 inline-block border border-white bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
          >
            Recommencer
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Le mot de passe doit faire au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Une erreur est survenue. Veuillez réessayer."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#001F3F]"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-white/60 hover:text-white mb-8"
        >
          <ArrowLeft size={12} /> Retour
        </Link>

        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
          Sécurité du compte
        </div>
        <h1 className="font-display mt-3 text-3xl sm:text-4xl font-bold uppercase tracking-tighter text-white">
          Nouveau<br />mot de passe
        </h1>

        {done ? (
          <div className="mt-10 border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="reset-success">
            <CheckCircle2 size={28} strokeWidth={1.5} className="text-emerald-400" />
            <h2 className="font-display mt-4 text-lg font-bold uppercase tracking-tight text-white">
              Mot de passe mis à jour
            </h2>
            <p className="mt-2 text-sm text-white/70">
              Vous allez être redirigé vers la page de connexion…
            </p>
          </div>
        ) : (
          <>
            <p className="mt-5 text-sm text-white/65 leading-relaxed">
              Choisissez un mot de passe d'au moins 8 caractères.
            </p>
            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="reset-form">
              <PasswordField
                label="Nouveau mot de passe"
                value={password}
                onChange={setPassword}
                show={show}
                onToggle={() => setShow((s) => !s)}
                testid="reset-password-input"
              />
              <PasswordField
                label="Confirmer le mot de passe"
                value={confirm}
                onChange={setConfirm}
                show={show}
                onToggle={() => setShow((s) => !s)}
                testid="reset-confirm-input"
                noToggle
              />

              {error && (
                <div
                  className="text-xs text-[#FF6B6B] border border-[#FF6B6B]/30 bg-[#FF6B6B]/5 px-3 py-2"
                  data-testid="reset-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full border border-white bg-white text-[#001F3F] hover:bg-[#FFD700] hover:border-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reset-submit-btn"
              >
                {submitting ? "Validation en cours…" : "Réinitialiser le mot de passe"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, testid, noToggle }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55 mb-2 block">
        {label}
      </label>
      <div className="relative">
        <Lock
          size={14}
          strokeWidth={1.5}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
        />
        <input
          type={show ? "text" : "password"}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border border-white/30 text-white px-10 py-3 pr-10 focus:outline-none focus:border-white"
          data-testid={testid}
        />
        {!noToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          >
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}
